/**
 * useConnectorSwagger Hook
 *
 * React Query hook for fetching Azure managed connector swagger specs.
 * The swagger spec contains full parameter definitions for operations.
 *
 * Optimization notes (based on LogicAppsUX patterns):
 * - ConnectorId is normalized (lowercase) for consistent cache keys
 * - Long cache time (24h) since swagger specs rarely change
 * - Only fetches for managed connectors (not built-in service providers)
 * - Swagger is pre-dereferenced to resolve $ref pointers once
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchConnectorSwagger } from '@/services/operationsService';
import { SwaggerParser, type DereferencedSwagger } from '@/parsers/SwaggerParser';
import { isManagedApiConnector, normalizeConnectorId } from '@/utils/connections';
import type { SwaggerSpec } from '@/types/swagger';

/**
 * Result type for useConnectorSwagger hook
 * Provides both raw swagger spec and parser instance
 */
export interface ConnectorSwaggerResult {
  /** Raw swagger spec (for direct property access) */
  swagger: DereferencedSwagger;
  /** SwaggerParser instance (for operation lookups) */
  parser: SwaggerParser;
}

// Query cache options - swagger specs rarely change
/** Shared cache freshness for connector swagger results. Long-lived because
 * swagger specs are practically immutable for a given connector version, and
 * dereferencing them is expensive. Exported so imperative callers (e.g.
 * `queryClient.fetchQuery` in the managed MCP add flow) can match the hook's
 * staleness window and dedupe against the same cache entry. */
export const CONNECTOR_SWAGGER_STALE_TIME = 1000 * 60 * 60 * 8; // 8 hours

const QUERY_OPTIONS = {
  staleTime: CONNECTOR_SWAGGER_STALE_TIME,
  gcTime: CONNECTOR_SWAGGER_STALE_TIME,
  refetchOnMount: true as const, // retry on remount (handles transient failures)
  refetchOnWindowFocus: false,
  refetchOnReconnect: true as const, // retry on reconnect
  retry: 2,
};

/**
 * Connector-specific swagger patches for azureagentservice InvokeAgent.
 * The Responses API body schema from APIM includes many fields that are
 * not needed (or invalid) when invoking via an agent reference.
 * Hide everything except the fields the user actually needs.
 */
const VISIBLE_INVOKE_AGENT_FIELDS = new Set(['agent', 'input']);

function patchSwagger(swagger: SwaggerSpec, connectorId: string): SwaggerSpec {
  if (!connectorId.toLowerCase().includes('azureagentservice')) return swagger;

  const hideNonVisibleProps = (properties: Record<string, unknown>) => {
    for (const [name, prop] of Object.entries(properties)) {
      if (VISIBLE_INVOKE_AGENT_FIELDS.has(name)) continue;
      if (prop && typeof prop === 'object') {
        (prop as Record<string, unknown>)['x-ms-visibility'] = 'internal';
      }
    }
  };

  // Patch definitions (body params usually $ref to these before dereferencing)
  if (swagger.definitions) {
    for (const [defName, def] of Object.entries(swagger.definitions)) {
      if (!def.properties) continue;
      // Only patch definitions that look like the InvokeAgent request body
      if (!defName.toLowerCase().includes('invoke') && !('agent' in def.properties && 'input' in def.properties)) continue;
      hideNonVisibleProps(def.properties);
    }
  }

  // Also patch inline schemas (after dereferencing, properties are inline)
  for (const pathItem of Object.values(swagger.paths)) {
    const op = pathItem.post ?? pathItem.put;
    if (!op) continue;
    if (op.operationId?.toLowerCase() !== 'invokeagent') continue;

    for (const param of op.parameters ?? []) {
      if (param.in !== 'body' || !param.schema?.properties) continue;
      hideNonVisibleProps(param.schema.properties as Record<string, unknown>);
    }
  }
  return swagger;
}

/**
 * Imperatively fetch + parse a managed connector's swagger spec.
 *
 * Shared by the {@link useConnectorSwagger} hook and imperative callers
 * (e.g. resolving an MCP server path at selection time). Returns the same
 * {@link ConnectorSwaggerResult} shape the hook caches.
 */
export async function fetchConnectorSwaggerResult(
  projectName: string,
  accessToken: string,
  connectorId: string
): Promise<ConnectorSwaggerResult> {
  const normalizedConnectorId = normalizeConnectorId(connectorId) ?? connectorId;

  const swagger = await fetchConnectorSwagger(
    { projectName, accessToken },
    normalizedConnectorId
  );

  if (!swagger) throw new Error(`No swagger for ${normalizedConnectorId}`);

  patchSwagger(swagger, normalizedConnectorId);
  const dereferenced = await SwaggerParser.parse(swagger);
  patchSwagger(dereferenced, normalizedConnectorId);

  return {
    swagger: dereferenced,
    parser: new SwaggerParser(dereferenced),
  };
}

/** Build the React Query cache key for a connector's swagger result. */
export function connectorSwaggerQueryKey(connectorId: string | null): [string, string | null] {
  return ['connectorSwagger', normalizeConnectorId(connectorId)];
}

/**
 * Hook to fetch swagger spec for an Azure managed connector
 *
 * @param connectorId - The full connector ID (e.g., /subscriptions/.../managedApis/amazonsqs)
 * @returns Query result with swagger spec
 */
export const useConnectorSwagger = (connectorId: string | null) => {
  const { getOttoToken } = useAuth();
  const { selectedApp } = useAppStore();

  // Normalize connectorId for consistent cache keys
  const normalizedConnectorId = useMemo(
    () => normalizeConnectorId(connectorId),
    [connectorId]
  );

  const isEnabled = !!selectedApp?.projectName && !!normalizedConnectorId && isManagedApiConnector(normalizedConnectorId);

  return useQuery<ConnectorSwaggerResult | null>({
    // Use normalized (lowercase) connectorId for cache key consistency
    queryKey: connectorSwaggerQueryKey(connectorId),
    queryFn: async () => {
      if (!selectedApp?.projectName || !connectorId) {
        throw new Error('Missing projectName or connectorId');
      }

      const token = await getOttoToken();
      if (!token) throw new Error('No auth token');

      return fetchConnectorSwaggerResult(selectedApp.projectName, token, connectorId);
    },
    enabled: isEnabled,
    ...QUERY_OPTIONS,
  });
};
